import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { canAccessPage } from './utils/roleUtils';
import LoginPage from './page/login';
import HealthBook from './page/healthbook';
import Template from './page/template';
import HealthRecordsHistory from './page/HealthRecordsHistory';
import ExamineeGroup from './page/ExamineeGroup';
import Campaign from './page/Campaign';
import UserManagement from './page/UserManagement';
import DashboardOrganization from './page/DashboardOrganization';

// Global styles
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Prompt', sans-serif;
  }
  
  html, body {
    font-family: 'Prompt', sans-serif;
    margin: 0;
    padding: 0;
    height: 100%;
    min-width: 1200px;
  }
  
  #root {
    height: 100vh;
    min-width: 1200px;
  }
`;

const AppContainer = styled.div`
  font-family: 'Prompt', sans-serif;
  min-height: 100vh;
`;

// Protected Route Component with Role-Based Authorization
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // แสดง loading ขณะกำลังโหลดข้อมูล auth
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.25rem',
        color: '#666'
      }}>
        กำลังโหลด...
      </div>
    );
  }

  // ⭐ ตรวจสอบการ login - ต้องมีทั้ง isAuthenticated และ user object
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // ดึง role และ path ปัจจุบัน
  const userRoles = user?.role || [];
  const userLevel = user?.level || null; // ⭐ เก็บไว้เพื่อส่งต่อไประบบอื่น (ไม่ได้ใช้ในการตรวจสอบสิทธิ์)
  const currentPath = location.pathname;

  // ⭐ ถ้า user มี token แต่ยังไม่มี role ให้รอก่อน
  // กรณีนี้เกิดขึ้นได้เมื่อ localStorage มี user แต่ไม่มี role property
  if (user && (!userRoles || userRoles.length === 0)) {
    console.warn('⚠️ User exists but no roles found, waiting for role data...');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.25rem',
        color: '#666'
      }}>
        กำลังตรวจสอบสิทธิ์...
      </div>
    );
  }

  // ตรวจสอบ role authorization (ใช้แค่ role array, ไม่ใช้ level)
  if (!canAccessPage(userRoles, currentPath, userLevel)) {
    console.warn('🚫 Access denied:', currentPath, 'User roles:', userRoles);

    // ⭐ Redirect ตาม role priority (สูงสุดไปต่ำสุด)
    // Priority: Super Admin > Admin > Admin Doctor > Manager > Analyze > Nurse

    // 1. ถ้าเป็น super_admin role
    if (userRoles.includes('super_admin')) {
      return <Navigate to="/healthbook" replace />;
    }

    // 2. ถ้าเป็น admin (dev/test user)
    if (userRoles.includes('admin')) {
      return <Navigate to="/healthbook" replace />;
    }

    // 3. ถ้าเป็น admin_doctor (หมอ admin)
    if (userRoles.includes('admin_doctor')) {
      return <Navigate to="/healthbook" replace />;
    }

    // 4. ถ้าเป็น manager
    if (userRoles.includes('manager')) {
      return <Navigate to="/health-groups" replace />;
    }

    // 5. ถ้าเป็น analyze
    if (userRoles.includes('analyze')) {
      return <Navigate to="/dashboard-organization" replace />;
    }

    // 6. ถ้าเป็น nurse
    if (userRoles.includes('nurse')) {
      return <Navigate to="/healthbook" replace />;
    }

    // ถ้าไม่มี role ที่รองรับ ให้ logout
    console.error('❌ User has no valid roles, logging out');
    return <Navigate to="/login" replace />;
  }

  // ผ่านการตรวจสอบทั้งหมด - แสดงหน้าได้
  return children;
};

// Routes Component
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/health-groups"
        element={
          <ProtectedRoute>
            <ExamineeGroup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <Campaign />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management"
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/healthbook"
        element={
          <ProtectedRoute>
            <HealthBook />
          </ProtectedRoute>
        }
      />
      <Route
        path="/template"
        element={
          <ProtectedRoute>
            <Template />
          </ProtectedRoute>
        }
      />
      <Route
        path="/health-records-history"
        element={
          <ProtectedRoute>
            <HealthRecordsHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard-organization"
        element={
          <ProtectedRoute>
            <DashboardOrganization />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <GlobalStyle />
        <AppContainer>
          <AppRoutes />
        </AppContainer>
      </AuthProvider>
    </Router>
  );
};

export default App;